import { createContext, useContext } from "react";

import memoize from "lodash/memoize";

import { RegalAuthProfile } from "Types/Auth";

export type RegalAuthContextValue = {
    user: {
        oktaUserId: string;
        email: string;
        firstName: string;
        lastName: string;
        name: string;
        roles: string[];
    };
    brand: {
        slug: string;
    };
};

const COOKIE_NAME = "regal-sid-profile";

const parseCookiesString = memoize((cookie: string) =>
    cookie.split(";").reduce<Record<string, string>>((acc, cookie) => {
        const [key, value] = cookie.split("=");
        acc[key.trim()] = value;
        return acc;
    }, {})
);

const getCookieDict = () => parseCookiesString(document.cookie);

const parseTeams = (teams: string | string[] | undefined): string[] => {
    if (teams === undefined) {
        return [];
    }
    if (typeof teams === "string") {
        try {
            const parsed: unknown = JSON.parse(teams);
            if (!Array.isArray(parsed)) {
                return [];
            }
            return parsed.filter((team): team is string => typeof team === "string");
        } catch {
            return [];
        }
    }
    return teams.filter((team): team is string => typeof team === "string");
};

const getProfileFromBase64Cookie = (cookie: string): RegalAuthProfile => {
    const parsed = atob(cookie);
    if (parsed.startsWith("{")) {
        const decoded = JSON.parse(parsed) as Omit<RegalAuthProfile, "teams"> & {
            teams?: string | string[];
        };
        return {
            ...decoded,
            teams: parseTeams(decoded.teams),
        };
    }

    const decoded = Object.fromEntries(new URLSearchParams(parsed)) as Omit<RegalAuthProfile, "roles" | "teams"> & {
        roles: string;
        teams?: string;
    };

    return {
        ...decoded,
        roles: decoded.roles.split(","),
        teams: parseTeams(decoded.teams),
    };
};

export const regalAuthProfileExists = (): boolean => {
    const cookie = getCookieDict()[COOKIE_NAME];
    return !!cookie;
};

export type RegalAuthLoadResult = RegalAuthContextValue & { teams: string[] };

export const loadRegalAuthProfile = (): RegalAuthLoadResult | undefined => {
    const cookie = getCookieDict()[COOKIE_NAME];

    if (!cookie) {
        return;
    }

    let profile: RegalAuthProfile;
    const uriDecodedCookie = decodeURIComponent(cookie);
    if (cookie.includes(".")) {
        const [content] = uriDecodedCookie.split(".");
        if (!content) {
            return;
        }
        profile = getProfileFromBase64Cookie(content);
    } else {
        profile = getProfileFromBase64Cookie(uriDecodedCookie);
    }

    return {
        user: {
            oktaUserId: profile.oktaUserId,
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            name: profile.name,
            roles: profile.roles,
        },
        brand: {
            slug: profile.brandSlug,
        },
        teams: profile.teams ?? [],
    };
};

export const RegalAuthContext = createContext<RegalAuthContextValue>({
    user: {
        oktaUserId: "",
        email: "",
        firstName: "",
        lastName: "",
        name: "",
        roles: [],
    },
    brand: {
        slug: "",
    },
});

export const RegalAuthProvider = ({
    children,
    profile,
}: {
    children: React.ReactNode;
    profile: RegalAuthContextValue;
}) => <RegalAuthContext.Provider value={profile}>{children}</RegalAuthContext.Provider>;

export const useRegalAuthContext = () => useContext(RegalAuthContext);
